import { ArrowLeftIcon, CopyAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Stack } from '@prophecy/ui-v3/Layout';
import { SlidingPanel } from '@prophecy/ui-v3/SlidingPanel';
import { Text } from '@prophecy/ui-v3/Typography';
import styled from 'styled-components';

import { PipelineRunRecord, SQLPipelineRunStatus } from '../types';
import { getRunStatusBadge } from '../utils';
import { CancelInfo } from './CancelInfo';
import { TriggerInfo } from './TriggerInfo';
import { ResolvePerson } from './useRunPersonResolver';

const MessagePreview = styled.pre`
  margin: 0;
  padding: ${theme.spaces.x12};
  max-height: 200px;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: ${theme.fontSizes.x13};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x6};
`;

// 20% narrower than the smallest preset ('s' is 50% / min 700px)
const DRAWER_STYLE = { width: '40%', minWidth: '560px' };

const MESSAGE_TITLE: Record<string, string> = {
  [SQLPipelineRunStatus.WARNING]: 'Warning',
  [SQLPipelineRunStatus.CANCELLED]: 'Cancellation details'
};

export function RunDetailsDrawer({
  run,
  teamId,
  resolvePerson,
  hasRun,
  onSelectRun,
  onBack,
  onShowMessage,
  onClose
}: {
  run: PipelineRunRecord;
  teamId?: string;
  resolvePerson: ResolvePerson;
  hasRun: (runId: string) => boolean;
  onSelectRun: (runId: string) => void;
  /** Set only when an earlier run is open underneath this one. */
  onBack?: () => void;
  onShowMessage: () => void;
  onClose: () => void;
}) {
  const isCancelled = run.runStatus === SQLPipelineRunStatus.CANCELLED;
  const messageTitle = run.errorMessage ? MESSAGE_TITLE[run.runStatus] : undefined;

  return (
    <SlidingPanel size='s' style={DRAWER_STYLE} open onClose={onClose}>
      <Stack gap={theme.spaces.x24} data-test-id='sql-pipeline-run-details-drawer'>
        <Stack gap={theme.spaces.x6}>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
            {onBack && (
              <Button
                variant='secondaryGray'
                size='s'
                icon={<ArrowLeftIcon type='default' size='xs' />}
                tooltip='Back to the previous run'
                aria-label='Back to the previous run'
                onClick={onBack}
                data-test-id='sql-pipeline-run-details-back-button'
              />
            )}
            <Text level='lg' weight={theme.fontWeight.medium}>
              {run.pipelineName}
            </Text>
            {getRunStatusBadge(run.runStatus)}
          </Stack>
          <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
            <Text level='xs' tone={theme.colors.gray500}>
              Run {run.runId}
            </Text>
            <Button
              variant='linkGray'
              size='xs'
              tooltip='Copy run id'
              aria-label='Copy run id'
              icon={<CopyAIcon type='default' size='xs' />}
              onClick={() => navigator.clipboard.writeText(run.runId)}
              data-test-id='sql-pipeline-run-details-copy-run-id'
            />
          </Stack>
        </Stack>

        <TriggerInfo
          triggeredBy={run.triggeredBy}
          parentRunId={run.parentRunId}
          rerunOfRunId={run.rerunOfRunId}
          resolvePerson={resolvePerson}
          teamId={teamId}
          hasRun={hasRun}
          onSelectRun={onSelectRun}
        />

        {isCancelled && <CancelInfo cancelledBy={run.cancelledBy} resolvePerson={resolvePerson} teamId={teamId} />}

        {messageTitle && (
          <Stack gap={theme.spaces.x12}>
            <Stack direction='horizontal' align='space-between' alignY='center'>
              <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.colors.gray500}>
                {messageTitle}
              </Text>
              <Button
                variant='linkGray'
                size='xs'
                onClick={onShowMessage}
                data-test-id='sql-pipeline-run-details-show-message'>
                View full details
              </Button>
            </Stack>
            <MessagePreview data-test-id='sql-pipeline-run-details-message'>{run.errorMessage}</MessagePreview>
          </Stack>
        )}
      </Stack>
    </SlidingPanel>
  );
}
